import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
// Seus ícones
import { FaHtml5, FaSass, FaReact, FaAngular, FaPhp, FaFigma, FaLinux, FaWordpress, FaLaravel, FaGulp } from 'react-icons/fa';
import { SiJavascript, SiCss3, SiTypescript, SiFirebase, SiGit, SiAdobephotoshop, SiBootstrap, SiMaterialdesign, SiMaterialui } from 'react-icons/si';

const skillsData = [
  {id: 1, title: 'HTML', icon: <FaHtml5 />, color: '#E34F26'},
  {id: 2, title: 'CSS', icon: <SiCss3 />, color: '#1572B6'},
  {id: 3, title: 'SASS', icon: <FaSass />, color: '#CC6699'},
  {id: 4, title: 'Bootstrap', icon: <SiBootstrap />, color: '#7952B3'},
  {id: 5, title: 'React', icon: <FaReact />, color: '#61DAFB'},
  {id: 5, title: 'Angular', icon: <FaAngular />, color: '#61DAFB'},
  {id: 6, title: 'JavaScript', icon: <SiJavascript />, color: '#F7DF1E'},
  {id: 7, title: 'TypeScript', icon: <SiTypescript />, color: '#3178C6'},
  {id: 8, title: 'Git', icon: <SiGit />, color: '#F05032'},
  {id: 9, title: 'Figma', icon: <FaFigma />, color: '#F24E1E'},
  {id: 10, title: 'Material', icon: <SiMaterialdesign />, color: '#757575'},
  {id: 11, title: 'MUI', icon: <SiMaterialui />, color: '#007FFF'},
  {id: 12, title: 'Linux', icon: <FaLinux />, color: '#FCC624'},
  {id: 13, title: 'Firebase', icon: <SiFirebase />, color: '#FFCA28'},
  {id: 14, title: 'WordPress', icon: <FaWordpress />, color: '#21759B'},
  {id: 15, title: 'PHP', icon: <FaPhp />, color: '#31A8FF'},
  {id: 16, title: 'Laravel', icon: <FaLaravel />, color: '#FF2D20'},
  {id: 17, title: 'Gulp', icon: <FaGulp />, color: '#DB4446'},
];

// Função que recalcula posições baseada no tamanho da tela
const generatePositions = (count, isMobile) => {
  const positions = [];
  
  // No mobile, usamos raios maiores na vertical para espalhar (elipse)
  // Desktop: Círculo (raio X e Y parecidos)
  // Mobile: Oval (raio X pequeno, raio Y grande)
  const minRadiusX = isMobile ? 35 : 20; 
  const maxRadiusX = isMobile ? 45 : 40;
  
  const minRadiusY = isMobile ? 20 : 20;
  const maxRadiusY = isMobile ? 40 : 40;

  for (let i = 0; i < count; i++) {
    const angleSlice = (360 / count);
    // Adicionei mais aleatoriedade para parecer natural
    const angle = (angleSlice * i) + (Math.random() * 15 - 7.5); 
    
    // Raio aleatório
    const radiusX = minRadiusX + Math.random() * (maxRadiusX - minRadiusX);
    const radiusY = minRadiusY + Math.random() * (maxRadiusY - minRadiusY);
    
    // Matemática da Elipse
    const left = 50 + radiusX * Math.cos(angle * Math.PI / 180);
    const top = 50 + radiusY * Math.sin(angle * Math.PI / 180);
    
    positions.push({ left: `${left}%`, top: `${top}%` });
  }
  return positions;
};

export default function SkillsCloud() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [positions, setPositions] = useState([]);

  // Detecta mudança de tela para recalcular posições
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setPositions(generatePositions(skillsData.length, mobile));
    };

    // Gera inicial
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
  <div id="skillsCloud">
    <div style={{
      position: 'relative',
      height: isMobile ? '70vh' : '90vh', // Mobile pode ser um pouco menor
      width: '100%',
      backgroundColor: '#050505',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      
      {/* 1. O Texto Central (Agora com zIndex alto para garantir que fique por cima) */}
      <div style={{ 
        position: 'relative', 
        zIndex: 50, // Importante: Garante que o texto fique ACIMA dos ícones se eles passarem perto
        textAlign: 'center',
        background: 'radial-gradient(circle, rgba(5,5,5,0.8) 0%, rgba(5,5,5,0) 70%)', // Sombra suave atrás do texto para leitura
        padding: '20px',
        borderRadius: '50%'
      }}>
        <motion.h2 
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          style={{ 
            fontSize: isMobile ? '2.5rem' : '4rem', // Fonte menor no mobile
            fontWeight: 'bold', 
            color: '#fff',
            marginBottom: '5px',
            lineHeight: 1.1
          }}
        >
          Minhas <span style={{ color: '#a78bfa' }}>Skills</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          style={{ color: '#aaa', fontSize: isMobile ? '1rem' : '1.2rem' }}
        >
          Stack e Tecnologias
        </motion.p>
      </div>

      {/* 2. Os Ícones Flutuantes */}
      {positions.length > 0 && skillsData.map((skill, index) => (
        <motion.div
          key={skill.id}
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ 
            delay: index * 0.05, 
            type: "spring",
            stiffness: 50 
          }}
          // Flutuação Suave
          animate={{ 
            y: [0, -10, 0], 
          }}
          transition={{
            y: {
              duration: 2 + Math.random() * 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
          style={{
            position: 'absolute',
            left: positions[index]?.left,
            top: positions[index]?.top,
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}
        >
          {/* O Card do Ícone */}
          <motion.div
            whileHover={{ scale: 1.2, zIndex: 100, boxShadow: `0 0 20px ${skill.color}` }}
            style={{
              width: isMobile ? '45px' : '75px', // ÍCONES MENORES NO MOBILE
              height: isMobile ? '45px' : '75px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.03)', // Mais sutil
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff', // Ícone branco
              fontSize: isMobile ? '1.2rem' : '2rem',
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              transition: 'border-color 0.3s'
            }}
          >
            {/* Ícone com a cor original ao passar o mouse ou branco padrão */}
            <span style={{ display: 'flex' }}>{skill.icon}</span>
          </motion.div>
        </motion.div>
      ))}

      {/* Luz de Fundo (Gradient Orb) */}
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '300px' : '600px', // Luz menor no mobile
        height: isMobile ? '300px' : '600px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />

    </div>
   </div>
  );
}